import React from 'react';
import type { GbuFassungInhalt, GbuUnterschrift, UnterschriftRolle } from '../../../../services/sifa/gbuTypes';
import { risikoStufe } from '../../../../services/sifa/gbuLogic';
import { SignaturePad } from '../../../common/SignaturePad';
import { TeilnahmeRahmen } from '../../public/TeilnahmeRahmen';
import {
  ALS_LINK,
  EINGABE,
  FEHLERKASTEN,
  HINWEISKASTEN,
  KNOPF_PRIMAER,
  KNOPF_SEKUNDAER,
  TEXTLINK,
} from '../../public/teilnahmeSeiteStil';
import { useGbuFreigabe, type PdfStand } from './useGbuFreigabe';
import {
  ANLASS_LABEL,
  ENDANSICHT_TEXTE,
  NAME_MAX,
  RISIKO_ANZEIGE,
  ROLLE_LABEL,
  UNTERSCHRIFT_MAX_BREITE,
  anzahlHochNachher,
  bereichZeile,
  eingabeFehler,
  fehlendeAngaben,
  fehltHinweis,
  formatBerlinerTag,
  formatDatum,
  kennungZeile,
  kurzZeilen,
  pdfDateiname,
  rollenTexte,
  type KurzZeile,
} from './gbuFreigabeSeiteLogic';

/** Reihenfolge der Rollen wie im Freigabeblock des PDF. */
const ROLLEN_FOLGE: readonly UnterschriftRolle[] = ['erstellt', 'geprueft', 'freigegeben'];

/** R als farbiges Kaestchen („R 6 hoch"). */
const RisikoMarke: React.FC<{ r: number; vorsatz: string }> = ({ r, vorsatz }) => {
  const a = RISIKO_ANZEIGE[risikoStufe(r)];
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-sm font-bold border rounded-lg ${a.klasse}`}>
      {vorsatz} R {r} <span className="font-semibold">· {a.label}</span>
    </span>
  );
};

/** Eine Gefaehrdung als Karte — am Handy lesbar, ohne seitliches Scrollen. */
const GefaehrdungKarte: React.FC<{ z: KurzZeile }> = ({ z }) => (
  <li className={`p-3 rounded-xl border space-y-2 ${z.hochNachher ? 'border-red-300 bg-red-50/40' : 'border-slate-200 bg-white'}`}>
    <p className="text-sm text-slate-500">
      Nr. {z.nr} · {z.faktorLabel}
    </p>
    <p className="font-semibold text-slate-900 break-words">{z.gefaehrdung}</p>
    {z.folgen && <p className="text-sm text-slate-600 break-words">Mögliche Folgen: {z.folgen}</p>}
    {z.bezug && <p className="text-sm text-slate-500 break-words">Betrifft: {z.bezug}</p>}
    <div className="flex flex-wrap items-center gap-2">
      <RisikoMarke r={z.rVor} vorsatz="vorher" />
      <span aria-hidden="true" className="text-slate-400">→</span>
      <RisikoMarke r={z.rNach} vorsatz="nachher" />
    </div>
    {z.hochNachher && (
      <p className="text-sm font-semibold text-red-700">Auch nach den Maßnahmen bleibt ein hohes Risiko.</p>
    )}
    {z.massnahmen.length > 0 ? (
      <ul className="space-y-1">
        {z.massnahmen.map((m) => (
          <li key={m.id} className="flex items-start gap-2 text-sm text-slate-700">
            <span
              title={m.artLabel}
              className="shrink-0 mt-0.5 w-6 h-6 flex items-center justify-center rounded-md bg-slate-800 text-white text-xs font-bold"
            >
              {m.art}
            </span>
            <span className="break-words">
              <span className="sr-only">{m.artLabel}: </span>
              {m.text}
            </span>
          </li>
        ))}
      </ul>
    ) : (
      <p className="text-sm text-slate-500">Keine Maßnahme eingetragen.</p>
    )}
  </li>
);

/** Kurzfassung: Rollen, Legende und die Gefaehrdungen. Alles Weitere steht im PDF. */
const Kurzfassung: React.FC<{ inhalt: GbuFassungInhalt; unterschriften: GbuUnterschrift[] }> = ({ inhalt, unterschriften }) => {
  const zeilen = kurzZeilen(inhalt);
  const hoch = anzahlHochNachher(zeilen);
  const rollen = ROLLEN_FOLGE.flatMap((rolle) => {
    const person = inhalt.rollen[rolle];
    return person && person.name.trim() ? [{ rolle, person }] : [];
  });

  return (
    <div className="space-y-5">
      {rollen.length > 0 && (
        <section className="space-y-2">
          <h2 className="text-lg font-bold text-slate-900">Beteiligte</h2>
          <ul className="space-y-1">
            {rollen.map(({ rolle, person }) => {
              const u = unterschriften.find((x) => x.rolle === rolle);
              return (
                <li key={rolle} className="text-slate-700 break-words">
                  <span className="font-semibold">{ROLLE_LABEL[rolle]}:</span> {person.name}
                  {person.funktion ? `, ${person.funktion}` : ''}
                  {u && <span className="text-sm text-emerald-700"> · unterschrieben am {formatBerlinerTag(u.datum)}</span>}
                </li>
              );
            })}
          </ul>
        </section>
      )}

      <section className="space-y-3">
        <h2 className="text-lg font-bold text-slate-900">Gefährdungen ({zeilen.length})</h2>
        <p className="text-sm text-slate-500">
          R = Wahrscheinlichkeit × Schwere (je 1–3): 1–2 gering, 3–4 mittel, 6–9 hoch. Maßnahmen nach der Rangfolge
          S (Substitution), T (technisch), O (organisatorisch), P (persönlich).
        </p>
        {hoch > 0 && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-base text-red-800">
            {hoch === 1
              ? 'Bei einer Gefährdung bleibt auch nach den Maßnahmen ein hohes Risiko (R ≥ 6). Sie ist rot markiert.'
              : `Bei ${hoch} Gefährdungen bleibt auch nach den Maßnahmen ein hohes Risiko (R ≥ 6). Sie sind rot markiert.`}
          </div>
        )}
        {zeilen.length === 0 ? (
          <div className={HINWEISKASTEN}>Diese Fassung enthält keine Gefährdungen.</div>
        ) : (
          <ol className="space-y-2">
            {zeilen.map((z) => <GefaehrdungKarte key={z.id} z={z} />)}
          </ol>
        )}
      </section>
    </div>
  );
};

/** „PDF ansehen" — danach immer auch als Link, falls der Browser das Fenster blockiert. */
// `aktualisiert`: nach der Unterschrift wird die Fassung neu geladen, bis
// dahin ruht der Knopf (sonst kaeme das PDF mit dem alten Stand).
const PdfBereich: React.FC<{ pdf: PdfStand; dateiname: string; aktualisiert: boolean; onAnsehen: () => void }> = ({
  pdf, dateiname, aktualisiert, onAnsehen,
}) => (
  <div className="space-y-2">
    {pdf.status === 'bereit' ? (
      <div className="flex flex-col sm:flex-row gap-2">
        <a href={pdf.url} target="_blank" rel="noopener" className={`${KNOPF_SEKUNDAER} ${ALS_LINK}`}>
          PDF öffnen
        </a>
        <a href={pdf.url} download={dateiname} className={`${KNOPF_SEKUNDAER} ${ALS_LINK}`}>
          PDF herunterladen
        </a>
      </div>
    ) : (
      <button type="button" onClick={onAnsehen} disabled={aktualisiert || pdf.status === 'erzeugt'} className={KNOPF_SEKUNDAER}>
        {aktualisiert ? 'PDF wird aktualisiert…' : pdf.status === 'erzeugt' ? 'PDF wird erstellt…' : 'PDF ansehen'}
      </button>
    )}
    {pdf.status === 'fehler' && <div className={FEHLERKASTEN}>{pdf.text}</div>}
  </div>
);

/** Ein Pflicht-Haekchen mit grosser Tippflaeche (wie auf der Teilnehmerseite). */
const Haekchen: React.FC<{ checked: boolean; disabled: boolean; onChange: (wert: boolean) => void; children: React.ReactNode }> = ({
  checked, disabled, onChange, children,
}) => (
  <label
    htmlFor="gbu-freigabe-gelesen"
    className={`flex items-start gap-3 p-4 min-h-[56px] rounded-xl border-2 cursor-pointer transition-all ${
      checked ? 'border-blue-600 bg-blue-50' : 'border-slate-200 bg-white hover:border-slate-300'
    }`}
  >
    <input
      id="gbu-freigabe-gelesen"
      type="checkbox"
      checked={checked}
      disabled={disabled}
      onChange={(e) => onChange(e.target.checked)}
      className="mt-0.5 w-6 h-6 shrink-0 accent-blue-600"
    />
    <span className="text-base text-slate-800 leading-snug">{children}</span>
  </label>
);

/** Erfolg bzw. „bereits benutzt" — der gruene Haken wie bei der Freimeldung. */
const Quittung: React.FC<{ titel: string; text: string; gruen: boolean }> = ({ titel, text, gruen }) => (
  <div className="space-y-3" aria-live="polite">
    <div className="flex items-center gap-3">
      <span
        aria-hidden="true"
        className={`flex items-center justify-center w-12 h-12 rounded-full shrink-0 ${gruen ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'}`}
      >
        <svg viewBox="0 0 24 24" className="w-7 h-7" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
          <path d="M5 13l4 4L19 7" />
        </svg>
      </span>
      <h2 className={`text-2xl font-bold ${gruen ? 'text-emerald-700' : 'text-slate-800'}`}>{titel}</h2>
    </div>
    <p className="text-slate-700">{text}</p>
    <p className="text-sm text-slate-500">Sie können dieses Fenster jetzt schließen.</p>
  </div>
);

/**
 * Oeffentliche Freigabeseite #/gbu-freigabe/<token> (docs/sifa-gbu-plan.md §8).
 *
 * Der Unternehmer (oder wer sonst den Link bekam) braucht kein Login: er
 * sieht die Kurzfassung der eingefrorenen Fassung und auf Wunsch das PDF,
 * traegt seinen Namen ein, bestaetigt das Lesen, unterschreibt und gibt frei.
 * Wird ohne Login gerendert (index.tsx routet am Auth-Gate vorbei, wie
 * #/freimeldung). Duenner Container: der Zustand lebt in useGbuFreigabe, die
 * Regeln in gbuFreigabeSeiteLogic.
 *
 * Am PC ohne Touch wird mit der Maus unterschrieben; die QR-Bruecke aufs
 * Handy (#/sign/<token>?u=1) ist fuer spaeter vorgesehen (§14.4 G).
 */
export const GbuFreigabeSeite: React.FC<{ token: string }> = ({ token }) => {
  const f = useGbuFreigabe(token);

  if (f.zustand === 'laedt') {
    return (
      <TeilnahmeRahmen>
        <p className="text-slate-500" aria-live="polite">Gefährdungsbeurteilung wird geladen…</p>
      </TeilnahmeRahmen>
    );
  }

  if (f.zustand === 'unbekannt' || f.zustand === 'abgelaufen' || f.zustand === 'schema' || f.zustand === 'netz' || !f.daten) {
    const art = f.zustand === 'unbekannt' || f.zustand === 'abgelaufen' || f.zustand === 'schema' ? f.zustand : 'netz';
    const t = ENDANSICHT_TEXTE[art];
    return (
      <TeilnahmeRahmen>
        <h1 className="text-xl font-bold text-slate-900">{t.titel}</h1>
        <div className={art === 'netz' ? FEHLERKASTEN : HINWEISKASTEN}>{t.text}</div>
        {art === 'netz' && (
          <button type="button" onClick={() => void f.neuLaden()} className={KNOPF_PRIMAER}>
            Erneut versuchen
          </button>
        )}
      </TeilnahmeRahmen>
    );
  }

  const { inhalt, rolle, gueltigBis } = f.daten;
  const texte = rollenTexte(rolle);

  // Am Handy liegt das Unterschriftsfeld ueber der ganzen Seite.
  if (f.padOffen && f.zustand === 'formular') {
    return (
      <SignaturePad
        alsOverlay
        titel={`Unterschrift — ${ROLLE_LABEL[rolle]}`}
        hinweis="Bitte im Feld unterschreiben. Die Unterschrift wird in der Gefährdungsbeurteilung abgedruckt."
        maxBreite={UNTERSCHRIFT_MAX_BREITE}
        uebernehmenLabel="Unterschrift übernehmen"
        onUebernehmen={f.unterschriftUebernehmen}
        onAbbrechen={f.padSchliessen}
        abbrechenLabel="Zurück"
      />
    );
  }

  const eingabe = { name: f.name, gelesen: f.gelesen, bild: f.bild };
  const hinweis = fehltHinweis(fehlendeAngaben(eingabe));
  const hartFehler = eingabeFehler(eingabe);
  const bereich = bereichZeile(inhalt.bereich);

  return (
    <TeilnahmeRahmen>
      <header className="space-y-1 pb-4 border-b border-slate-200">
        {inhalt.buero.name && <p className="text-sm font-medium text-slate-500">{inhalt.buero.name}</p>}
        <h1 className="text-2xl font-bold text-slate-900">{texte.titel}</h1>
        {inhalt.kunde.name && <p className="font-semibold text-slate-800 break-words">{inhalt.kunde.name}</p>}
        {inhalt.kunde.anschrift && <p className="text-sm text-slate-600 break-words">{inhalt.kunde.anschrift}</p>}
        <p className="pt-2 text-sm text-slate-500">{kennungZeile(inhalt)} · {ANLASS_LABEL[inhalt.anlass] ?? inhalt.anlass}</p>
        <p className="text-lg font-semibold text-slate-900 break-words">{inhalt.titel}</p>
        {bereich && <p className="text-slate-700 break-words">Arbeitsbereich: {bereich}</p>}
        {inhalt.bereich.beschreibung && <p className="text-sm text-slate-600 break-words">{inhalt.bereich.beschreibung}</p>}
        <p className="text-sm text-slate-500">
          Überprüfung bis {formatDatum(inhalt.pruefenBis)}
          {f.zustand === 'formular' && gueltigBis ? ` · Link gültig bis ${formatBerlinerTag(gueltigBis)}` : ''}
        </p>
      </header>

      {f.zustand === 'erfolg' && <Quittung titel={texte.erfolgTitel} text={texte.erfolgText} gruen />}
      {f.zustand === 'bereits' && <Quittung titel={texte.bereitsTitel} text={texte.bereitsText} gruen={false} />}

      {f.zustand === 'formular' && <p className="text-slate-600">{texte.einleitung}</p>}

      <PdfBereich pdf={f.pdf} dateiname={pdfDateiname(inhalt)} aktualisiert={f.nachlaedt} onAnsehen={() => void f.pdfAnsehen()} />

      {f.zustand === 'formular' && (
        <>
          <Kurzfassung inhalt={inhalt} unterschriften={f.unterschriften} />

          <section className="space-y-4 pt-4 border-t border-slate-200">
            <h2 className="text-lg font-bold text-slate-900">{texte.abschnitt}</h2>

            <div className="space-y-1.5">
              <label htmlFor="gbu-freigabe-name" className="block font-semibold text-slate-800">
                Ihr Name (Vor- und Nachname)
              </label>
              <input
                id="gbu-freigabe-name"
                type="text"
                autoComplete="name"
                maxLength={NAME_MAX}
                value={f.name}
                disabled={f.sendet}
                onChange={(e) => f.setName(e.target.value)}
                className={EINGABE}
              />
            </div>

            <Haekchen checked={f.gelesen} disabled={f.sendet} onChange={f.setGelesen}>
              {texte.haekchen}
            </Haekchen>

            {f.bild ? (
              <div className="space-y-2">
                <p className="font-semibold text-slate-800">Ihre Unterschrift</p>
                <div className="p-2 bg-white border border-slate-200 rounded-xl">
                  <img src={f.bild} alt="Ihre Unterschrift" className="max-h-28 w-auto mx-auto" />
                </div>
                <button type="button" onClick={f.unterschriftLoeschen} disabled={f.sendet} className={TEXTLINK}>
                  Unterschrift löschen und neu unterschreiben
                </button>
              </div>
            ) : (
              <button type="button" onClick={f.padOeffnen} disabled={f.sendet} className={KNOPF_SEKUNDAER}>
                Jetzt unterschreiben
              </button>
            )}

            {(f.fehler || hartFehler) && (
              <div className={FEHLERKASTEN} role="alert">
                {f.fehler || hartFehler}
              </div>
            )}
            {hinweis && !f.fehler && !hartFehler && (
              <div className={HINWEISKASTEN} aria-live="polite">
                {hinweis}
              </div>
            )}

            <button
              type="button"
              onClick={() => void f.freigeben()}
              disabled={f.sendet || !!hinweis || !!hartFehler}
              className={KNOPF_PRIMAER}
            >
              {f.sendet ? 'Wird übertragen…' : texte.knopf}
            </button>
          </section>
        </>
      )}
    </TeilnahmeRahmen>
  );
};

export default GbuFreigabeSeite;
